import { useQueryClient } from '@tanstack/react-query';
import { getToken, onMessage } from 'firebase/messaging';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { getFirebaseMessaging } from '../../firebase';
import { registerDevice } from '../../services/notificationService';
import { useAuthStore } from '../../stores/useAuthStore';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { resolveNotificationRoute } from '../notificationActionResolver';
import { NOTIFICATION_QUERY_KEY } from './useNotifications';

const useFirebaseNotifications = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const fcmToken = useNotificationStore((state) => state.fcmToken);
  const setFcmToken = useNotificationStore((state) => state.setFcmToken);
  const addNotification = useNotificationStore(
    (state) => state.addNotification
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    let unsubscribeForeground = () => {};

    const setup = async () => {
      // ── Step 1: Request browser notification permission ──────────────────
      // This is a plain browser API — works regardless of FCM support.
      // Must be called before any FCM-specific code so the prompt always shows.
      if (!('Notification' in window)) {
        console.warn('[FCM] Browser does not support notifications.');
        return;
      }

      // If already decided, don't prompt again — just check the state.
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.warn('[FCM] Notification permission:', permission);
        return;
      }

      // ── Step 2: Check FCM support & initialise messaging lazily ──────────
      // Separated so a failed support check doesn't skip the permission prompt.
      try {
        if (!('serviceWorker' in navigator)) {
          console.warn('[FCM] Service workers not supported.');
          return;
        }

        const messaging = await getFirebaseMessaging();
        if (!messaging) {
          console.warn(
            '[FCM] Firebase Messaging not supported in this browser.'
          );
          return;
        }

        // ── Step 3: Get/refresh FCM token ───────────────────────────────────
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });

        console.log('FCM Token', token);

        if (!token) {
          console.warn(
            '[FCM] Unable to generate device token. Check VAPID key and service worker registration.'
          );
          return;
        }

        // Only register with backend when token is new or has rotated
        if (token !== fcmToken) {
          await registerDevice(token);
          setFcmToken(token);
        }

        // ── Step 4: Foreground message listener ─────────────────────────────
        unsubscribeForeground = onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          const data = payload.data || {};

          // Parse params safely
          let parsedParams = {};
          if (data.params) {
            try {
              parsedParams = JSON.parse(data.params);
            } catch {
              parsedParams = {};
            }
          }

          // Build a normalised notification matching the store shape
          const normalisedNotification = {
            id: data.id ? Number(data.id) : Date.now(),
            title: title || 'New Notification',
            message: body || '',
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
              action_type: data.action_type || null,
              params: parsedParams,
              id: data.id || null,
            },
          };

          addNotification(normalisedNotification);

          queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });

          const route = resolveNotificationRoute(
            data.action_type,
            parsedParams
          );

          toast(
            (t) => (
              <div
                className="flex flex-col gap-1 cursor-pointer"
                onClick={() => {
                  toast.dismiss(t.id);
                  if (route) window.location.href = route;
                }}
              >
                <span className="font-bold text-sm">
                  {title || 'New Notification'}
                </span>
                <span className="text-xs text-gray-600">{body}</span>
              </div>
            ),
            { duration: 6000, position: 'top-right', icon: '🔔' }
          );
        });
      } catch (err) {
        console.error('[FCM] Setup error:', err);
      }
    };

    setup();

    // Re-fetch when the user's tab regains focus (cross-tab sync)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeForeground();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useFirebaseNotifications;
